import { Add, Delete, Edit } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid';
import { ReactElement, useMemo, useState } from 'react';
import { StyledDataGrid } from '../../../shared-theme/StyledComponents';

// Generic CRUD panel for string-PK reference catalogs (Action, UserType, ObjectType,
// OrganizationType, LocationType, WorkflowType/State/TaskState). Each consumer wires
// the RTK Query hooks + a small set of arg-builder callbacks; this component handles
// the DataGrid, modals, and snackbar plumbing.
//
// Why arg-builders instead of a more declarative API? The RTK codegen output uses
// per-endpoint argument keys like `handlersCreateActionRequest`, and the path params
// are also per-endpoint (`actionName`, `objectType`, etc.). Centralizing those names
// into one config object makes the manifest entries verbose; exposing tiny
// per-mutation callbacks keeps the call sites readable.

export type AdminCatalogFieldType =
  | 'text'
  | 'textarea'
  | 'boolean'
  | 'select';

export interface AdminCatalogSelectOption {
  value: string;
  label: string;
}

export interface AdminCatalogField {
  /** Field name in the request body and response object (e.g., "description"). */
  name: string;
  label: string;
  type: AdminCatalogFieldType;
  required?: boolean;
  /** True for the PK field on Edit (not modifiable post-create). */
  immutableOnEdit?: boolean;
  helperText?: string;
  /** Hide this field from the auto-generated DataGrid columns. */
  hideInGrid?: boolean;
  /**
   * Select-field options for `type: 'select'`. The consumer panel is responsible
   * for calling any list-fetch hook (e.g. useGetApiLocationTypesQuery) and
   * mapping the result into `{ value, label }[]` — keeping that hook outside
   * AdminCatalogPanel respects React's Rules of Hooks regardless of how the
   * `fields` array is constructed.
   */
  options?: AdminCatalogSelectOption[];
}

export interface AdminCatalogPanelProps<
  TItem extends GridValidRowModel = GridValidRowModel,
> {
  tableName: string;
  apiPath: string;
  /** Field name on the response item that holds the primary key (e.g., "actionName"). */
  pkAccessor: keyof TItem & string;

  // RTK Query hooks. Loose `any` typing — full generic typing across RTK codegen
  // result types isn't worth the complexity here.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  useList: () => {
    data?: TItem[] | { items?: TItem[] };
    isLoading?: boolean;
    isFetching?: boolean;
    refetch?: () => unknown;
  };
  useCreate: () => readonly [(arg: any) => any, { isLoading: boolean }];
  useUpdate?: () => readonly [(arg: any) => any, { isLoading: boolean }];
  useDelete: () => readonly [(arg: any) => any, { isLoading: boolean }];

  /** Maps form values → the create-mutation arg object. */
  buildCreateArg: (formValues: Record<string, unknown>) => unknown;
  /** Maps the row + edited form values → the update-mutation arg object. */
  buildUpdateArg?: (
    row: TItem,
    formValues: Record<string, unknown>,
  ) => unknown;
  /** Maps the row → the delete-mutation arg object (typically just the PK param). */
  buildDeleteArg: (row: TItem) => unknown;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  fields: AdminCatalogField[];
  /** Custom column overrides; if omitted, columns are auto-generated from `fields`. */
  columns?: GridColDef<TItem>[];
  /** Message shown when DELETE returns 409 (FK guard). */
  fkConflictMessage?: string;
}

interface SnackState {
  msg: string;
  severity: 'success' | 'error' | 'info';
}

function AdminCatalogPanel<TItem extends GridValidRowModel = GridValidRowModel>({
  tableName,
  apiPath,
  pkAccessor,
  useList,
  useCreate,
  useUpdate,
  useDelete,
  buildCreateArg,
  buildUpdateArg,
  buildDeleteArg,
  fields,
  columns: customColumns,
  fkConflictMessage,
}: AdminCatalogPanelProps<TItem>) {
  const listResult = useList();
  const rows: TItem[] = useMemo(() => {
    const data = listResult?.data;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.items ?? [];
  }, [listResult?.data]);

  const [createMutation, { isLoading: creating }] = useCreate();
  const updateHook = useUpdate?.() ?? null;
  const updateMutation = updateHook?.[0] ?? null;
  const updating = updateHook?.[1]?.isLoading ?? false;
  const [deleteMutation, { isLoading: deleting }] = useDelete();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<TItem | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [confirmRow, setConfirmRow] = useState<TItem | null>(null);
  const [snack, setSnack] = useState<SnackState | null>(null);

  const isEdit = !!editingRow;

  const openAdd = () => {
    setEditingRow(null);
    setFormValues({});
    setModalOpen(true);
  };
  const openEdit = (row: TItem) => {
    setEditingRow(row);
    setFormValues(
      fields.reduce<Record<string, unknown>>((acc, f) => {
        acc[f.name] = (row as Record<string, unknown>)[f.name] ?? '';
        return acc;
      }, {}),
    );
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
    setFormValues({});
  };

  const handleSubmit = async () => {
    try {
      if (isEdit && updateMutation && editingRow && buildUpdateArg) {
        const updateBody = fields.reduce<Record<string, unknown>>((acc, f) => {
          if (f.immutableOnEdit) return acc;
          acc[f.name] = formValues[f.name];
          return acc;
        }, {});
        await updateMutation(buildUpdateArg(editingRow, updateBody)).unwrap();
        setSnack({ msg: `${tableName} updated.`, severity: 'success' });
      } else {
        const createBody = fields.reduce<Record<string, unknown>>((acc, f) => {
          acc[f.name] = formValues[f.name];
          return acc;
        }, {});
        await createMutation(buildCreateArg(createBody)).unwrap();
        setSnack({ msg: `${tableName} created.`, severity: 'success' });
      }
      listResult?.refetch?.();
      closeModal();
    } catch (e: unknown) {
      setSnack({
        msg: extractErrorMessage(e) ?? 'Operation failed.',
        severity: 'error',
      });
    }
  };

  const handleDelete = async () => {
    if (!confirmRow) return;
    try {
      await deleteMutation(buildDeleteArg(confirmRow)).unwrap();
      setSnack({ msg: `${tableName} deleted.`, severity: 'success' });
      listResult?.refetch?.();
    } catch (e: unknown) {
      const status = (e as { status?: number; originalStatus?: number })?.status
        ?? (e as { originalStatus?: number })?.originalStatus;
      if (status === 409 && fkConflictMessage) {
        setSnack({ msg: fkConflictMessage, severity: 'error' });
      } else {
        setSnack({
          msg: extractErrorMessage(e) ?? 'Delete failed.',
          severity: 'error',
        });
      }
    } finally {
      setConfirmRow(null);
    }
  };

  const columns: GridColDef<TItem>[] = useMemo(() => {
    if (customColumns) return customColumns;
    const fieldCols: GridColDef<TItem>[] = fields
      .filter((f) => !f.hideInGrid)
      .map((f) => ({
        field: f.name,
        headerName: f.label,
        flex: f.type === 'textarea' ? 2 : 1,
        valueFormatter:
          f.type === 'boolean'
            ? (v: unknown) => (v ? 'Yes' : 'No')
            : f.type === 'select' && f.options
              ? (v: unknown) => {
                  const opt = f.options?.find((o) => o.value === v);
                  return opt ? opt.label : (v ?? '');
                }
              : undefined,
      }));
    const actionCol: GridColDef<TItem> = {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 100,
      getActions: ({ row }) => {
        const out: ReactElement<GridActionsCellItemProps>[] = [];
        if (updateMutation && buildUpdateArg) {
          out.push(
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              label="Edit"
              onClick={() => openEdit(row)}
            />,
          );
        }
        out.push(
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => setConfirmRow(row)}
          />,
        );
        return out;
      },
    };
    return [...fieldCols, actionCol];
  }, [customColumns, fields, updateMutation, buildUpdateArg]);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          Table: {tableName} · API: {apiPath}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={openAdd}
          disabled={creating}
          data-testid={`admin-catalog-add-${tableName.toLowerCase()}`}
        >
          Add {tableName}
        </Button>
      </Stack>

      <Box sx={{ height: 420, width: '100%' }}>
        <StyledDataGrid
          rows={rows}
          columns={columns as GridColDef[]}
          getRowId={(row) =>
            (row as Record<string, unknown>)[pkAccessor] as string
            ?? Math.random().toString(36)
          }
          loading={
            !!(
              listResult?.isLoading
              || listResult?.isFetching
              || creating
              || updating
              || deleting
            )
          }
          disableRowSelectionOnClick
          density="compact"
        />
      </Box>

      <Dialog open={modalOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEdit ? `Edit ${tableName}` : `Add ${tableName}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {fields.map((f) => {
              const disabled = isEdit && !!f.immutableOnEdit;
              if (f.type === 'boolean') {
                return (
                  <FormControlLabel
                    key={f.name}
                    control={
                      <Checkbox
                        checked={!!formValues[f.name]}
                        onChange={(e) =>
                          setFormValues({
                            ...formValues,
                            [f.name]: e.target.checked,
                          })
                        }
                        disabled={disabled}
                      />
                    }
                    label={f.label}
                  />
                );
              }
              if (f.type === 'select') {
                const options = f.options ?? [];
                const currentValue = (formValues[f.name] as string) ?? '';
                const selected =
                  options.find((o) => o.value === currentValue) ?? null;
                return (
                  <Autocomplete<AdminCatalogSelectOption>
                    key={f.name}
                    options={options}
                    value={selected}
                    onChange={(_, opt) =>
                      setFormValues({
                        ...formValues,
                        [f.name]: opt?.value ?? '',
                      })
                    }
                    getOptionLabel={(opt) => opt.label}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={f.label}
                        required={f.required}
                        helperText={f.helperText}
                      />
                    )}
                  />
                );
              }
              return (
                <TextField
                  key={f.name}
                  label={f.label}
                  value={(formValues[f.name] as string) ?? ''}
                  onChange={(e) =>
                    setFormValues({ ...formValues, [f.name]: e.target.value })
                  }
                  required={f.required}
                  multiline={f.type === 'textarea'}
                  rows={f.type === 'textarea' ? 3 : undefined}
                  disabled={disabled}
                  helperText={f.helperText}
                  size="small"
                  fullWidth
                />
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={creating || updating}
          >
            {isEdit ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmRow} onClose={() => setConfirmRow(null)}>
        <DialogTitle>Delete {tableName}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete{' '}
            <strong>
              {confirmRow
                ? String(
                    (confirmRow as Record<string, unknown>)[pkAccessor] ?? '',
                  )
                : ''}
            </strong>
            ? This cannot be undone.
            {fkConflictMessage && (
              <Box
                component="span"
                sx={{ display: 'block', mt: 1, fontSize: '0.85em' }}
              >
                If this {tableName.toLowerCase()} is referenced elsewhere, the
                server will reject the delete.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            variant="filled"
            onClose={() => setSnack(null)}
          >
            {snack.msg}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
}

function extractErrorMessage(e: unknown): string | undefined {
  if (!e || typeof e !== 'object') return undefined;
  const data = (e as { data?: unknown }).data;
  if (data && typeof data === 'object') {
    const msg = (data as { message?: string; error?: string; details?: string });
    return msg.message ?? msg.error ?? msg.details;
  }
  return undefined;
}

export default AdminCatalogPanel;
